import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { mergeMap, switchMap, map, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { FleetActions } from './fleet.actions';
import { RentedCarService } from '../../services/rented-car.service';

@Injectable()
export class FleetEffects {
  private actions$ = inject(Actions);
  private svc = inject(RentedCarService);

  loadCars$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FleetActions.loadCars),
      mergeMap(() => this.svc.getAll().pipe(
        map(cars => FleetActions.loadCarsSuccess({ cars })),
        catchError(err => of(FleetActions.loadCarsFailure({ error: err.message })))
      ))
    )
  );

  loadCar$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FleetActions.loadCar),
      mergeMap(({ id }) => this.svc.getById(id).pipe(
        map(car => FleetActions.loadCarSuccess({ car })),
        catchError(err => of(FleetActions.loadCarFailure({ error: err.message })))
      ))
    )
  );

  createCar$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FleetActions.createCar),
      mergeMap(({ dto }) => this.svc.create(dto).pipe(
        map(car => FleetActions.createCarSuccess({ car })),
        catchError(err => of(FleetActions.createCarFailure({ error: err.error?.error ?? err.message })))
      ))
    )
  );

  updateCar$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FleetActions.updateCar),
      mergeMap(({ id, dto }) => this.svc.update(id, dto).pipe(
        map(car => FleetActions.updateCarSuccess({ car })),
        catchError(err => of(FleetActions.updateCarFailure({ error: err.error?.error ?? err.message })))
      ))
    )
  );

  deleteCar$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FleetActions.deleteCar),
      mergeMap(({ id }) => this.svc.delete(id).pipe(
        map(() => FleetActions.deleteCarSuccess({ id })),
        catchError(err => of(FleetActions.deleteCarFailure({ error: err.message })))
      ))
    )
  );

  loadExtraCharges$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FleetActions.loadExtraCharges),
      switchMap(({ carId }) => this.svc.getExtraCharges(carId).pipe(
        map(charges => FleetActions.loadExtraChargesSuccess({ carId, charges })),
        catchError(err => of(FleetActions.loadExtraChargesFailure({ error: err.message })))
      ))
    )
  );

  addExtraCharge$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FleetActions.addExtraCharge),
      mergeMap(({ carId, dto }) => this.svc.addExtraCharge(carId, dto).pipe(
        map(charge => FleetActions.addExtraChargeSuccess({ charge })),
        catchError(err => of(FleetActions.addExtraChargeFailure({ error: err.message })))
      ))
    )
  );

  loadCostSummary$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FleetActions.loadCostSummary),
      switchMap(({ carId, from, to }) => this.svc.getCostSummary(carId, from, to).pipe(
        map(summary => FleetActions.loadCostSummarySuccess({ summary })),
        catchError(err => of(FleetActions.loadCostSummaryFailure({ error: err.message })))
      ))
    )
  );
}
